import { useConstant } from '@rcm/react-utils';
import classNames from 'classnames';
import { ChangeHandler } from 'forms';
import noop from 'lodash/noop';
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useEffectOnceWhen } from 'rooks';
import * as uuid from 'uuid';
import getReCaptcha2PublicConfig from '../functions/getReCaptcha2PublicConfig.client';
import initReCaptcha2 from '../functions/initReCaptcha2.client';
import useCaptchaActionEnabled from './useCaptchaActionEnabled.client';

interface UseReCaptcha2BehaviorOptions {
  /**
   * Current recaptcha action.
   */
  action: string;

  /**
   * Position of the ReCAPTCHA V2 badge.
   */
  badge?: 'bottomleft' | 'bottomright' | 'inline';

  /**
   * Tab index of the challenge.
   */
  challengeTabIndex?: number;

  /**
   * Additional classes for the captcha element
   */
  className?: string;

  /**
   * Passed as the name for the change handler callback.
   */
  name?: string;

  /**
   * Called when captcha authentication succeeds
   */
  onValueChange?: ChangeHandler<string>;

  /**
   * Called when an error occurs during captcha authentication
   */
  onError?: () => void;

  /**
   * Called after captcha response becomes invalid due to timeout
   */
  onExpired?: () => void;
}

/**
 * Current state of ReCAPTCHA V2 callbacks generated by useReCaptcha2Callbacks.
 */
interface UseReCaptcha2BehaviorResultState {
  /**
   * Dispatches an action to indicate that captcha flow has started.
   *
   * This should be called when the consuming component wants the
   * captcha flow to begin.
   *
   * An event may be passed. If passed, `preventDefault()` will be called.
   */
  beginCaptcha: (event?: SyntheticEvent) => void;

  /**
   * Whether or not captcha is enabled for the current action.
   */
  isEnabled: boolean;

  /**
   * Whether or not a CAPTCHA authentication is currently in progress
   */
  isLoading: boolean;

  /**
   * Attributes required by HTML elements that hook into ReCAPTCHA V2.
   */
  attrs: {
    className: string;
    'data-sitekey'?: string;
    'data-callback'?: string;
    'data-expired-callback'?: string;
    'data-error-callback'?: string;
  };
}

const uuidVarName = () => uuid.v4().replace(/-/g, '_');

/**
 * Triggers behavior for using Google ReCAPTCHA inside the current component.
 *
 * This hook invokes required behavior for Google ReCAPTCHA authentication to be
 * used within the current React component, produces the necessary HTML
 * attributes for an element, and tracks all related state.
 *
 * ## Badge
 *
 * The ReCAPTCHA V2 badge is required per Google ReCAPTCHA terms of service,
 * will be rendered automatically, and cannot be disabled. However, the logic
 * the API uses to insert the badge doesn't always work correctly. In this case,
 * you may set the `badge` property to `'inline'`, and use CSS to position it.
 *
 * ## Behavior When reCAPTCHA Is Disabled
 *
 * When reCAPTCHA is disabled for the given action, the majority of this
 * hook's behavior will be nullified. This behavior includes the following
 * changes:
 *
 * - No HTML attributes are provided besides `className`, and the only classes
 *   included are those passed in.
 * - No callbacks are added to the global scope upon mount, and no callbacks are
 *   removed from the global scope upon unmount.
 * - `initReCaptcha2` is never called, and so the reCAPTCHA script is never
 *   added to the DOM.
 * - The returned `beginCaptcha()` function causes `onValueChange(null)` to be
 *   called immediately and synchronously to simulate a completed captcha.
 *
 * ## Examples
 *
 * @example
 * ```js
 * // This example shows how this hook could be used to implement a minimal
 * // invisible ReCAPTCHA button.
 * //
 * // Note that RCM ReCaptcha2 already includes an implementation of a button
 * // component like this one. It is called `InvisibleReCaptcha2Button` and you
 * // should use that one if possible rather than implementing your own.
 *
 * import { useCallback } from 'react';
 * import { useReCaptcha2Behavior } from '@rcm/recaptcha-2';
 *
 * const CaptchaInput = () => {
 *  const onValueChange = useCallback(
 *    (
 *      newCaptchaResponse,
 *      oldCaptchaResponse,
 *      buttonName
 *    ) => {
 *      console.log(
 *        `Captcha response on button "${buttonName}": ${newCaptchaResponse}`
 *      );
 *    },
 *    []
 *  );
 *
 *  const onError = useCallback(() => {
 *    console.error('Captcha error occurred.');
 *  }, []);
 *
 *  const onExpired = useCallback(() => {
 *    console.error('Captcha expired.');
 *  }, []);
 *
 *  const {attrs, beginAuth, isLoading} = useReCaptcha2Behavior({
 *    action: 'my-action',
 *    className: 'btn btn-primary',
 *    name: 'myCaptchaButton',
 *    onValueChange,
 *    onError,
 *    onExpired,
 *  });
 *
 *  return (
 *    <button onClick={beginAuth} {...attrs}>
 *      {isLoading ? 'Authenticating...' : 'Click to continue.'}
 *    </button>
 *  );
 * };
 * ```
 */
const useReCaptcha2Behavior = ({
  action,
  badge,
  challengeTabIndex,
  className = '',
  name,
  onError = noop,
  onExpired = noop,
  onValueChange = noop,
}: UseReCaptcha2BehaviorOptions): UseReCaptcha2BehaviorResultState => {
  const isEnabled = useCaptchaActionEnabled(action);

  useEffectOnceWhen(() => {
    if (!isEnabled) {
      return;
    }

    initReCaptcha2();
  });

  // Load the site key from config
  const { siteKey } = useConstant(getReCaptcha2PublicConfig);

  // Track captcha authentication progress
  const [isLoading, setIsLoading] = useState(false);

  // Track captcha responses
  const [gReCaptchaResponse, setGReCaptchaResponse] = useState(null as string);

  // UUID used for naming global callbacks
  const cbUuid = useConstant(uuidVarName);

  // Global names of the callbacks
  const [cbErrorName, cbExpiredName, cbSuccessName] = [
    useMemo(() => `__RCM_RECAPTCHA_2_CB_${cbUuid}_ERROR`, [cbUuid]),
    useMemo(() => `__RCM_RECAPTCHA_2_CB_${cbUuid}_EXPIRED`, [cbUuid]),
    useMemo(() => `__RCM_RECAPTCHA_2_CB_${cbUuid}_SUCCESS`, [cbUuid]),
  ];

  // Fired when a captcha error occurs
  const cbError = useCallback(() => {
    setIsLoading(false);
    onError();
  }, [onError]);

  // Fired when captcha auth has expired
  const cbExpired = useCallback(() => {
    setIsLoading(false);
    onExpired();
  }, [onExpired]);

  // Fired when captcha auth has succeeded
  const cbSuccess = useCallback(
    (newGReCaptchaResponse: string) => {
      setIsLoading(false);
      setGReCaptchaResponse(newGReCaptchaResponse);
      onValueChange(newGReCaptchaResponse, gReCaptchaResponse, name);
    },
    [gReCaptchaResponse, name, onValueChange]
  );

  // Consumer should call this when captcha authentication is about to begin
  const beginCaptcha = useCallback(
    (event: SyntheticEvent) => {
      event.preventDefault();

      if (!isEnabled) {
        cbSuccess(null);
        return;
      }

      setIsLoading(true);
    },
    [isEnabled, cbSuccess]
  );

  // Register callbacks globally so ReCAPTCHA can see them
  useEffect(() => {
    if (!isEnabled) {
      return noop;
    }

    Object.assign(window, {
      [cbErrorName]: cbError,
      [cbExpiredName]: cbExpired,
      [cbSuccessName]: cbSuccess,
    });

    return () => {
      delete window[cbErrorName];
      delete window[cbExpiredName];
      delete window[cbSuccessName];
    };
  }, [cbError, cbErrorName, cbExpired, cbExpiredName, cbSuccess, cbSuccessName, isEnabled]);

  return useMemo(
    () => ({
      beginCaptcha,
      isEnabled,
      isLoading,
      attrs: {
        ...(!isEnabled
          ? { className }
          : {
              className: classNames(className, 'g-recaptcha'),
              ...(badge ? { 'data-badge': badge } : {}),
              'data-callback': cbSuccessName,
              'data-error-callback': cbErrorName,
              'data-expired-callback': cbExpiredName,
              'data-sitekey': siteKey,
              ...(challengeTabIndex != null ? { 'data-tabindex': challengeTabIndex } : {}),
            }),
      },
    }),
    [
      badge,
      beginCaptcha,
      cbErrorName,
      cbExpiredName,
      cbSuccessName,
      challengeTabIndex,
      className,
      isEnabled,
      isLoading,
      siteKey,
    ]
  );
};

export default useReCaptcha2Behavior;
